import _ from 'lodash';

import Joi from 'joi';
import PropTypes from 'prop-types';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Form from '../../common/form';

import toastService from '../../../services/toastService';
import withAnomalySchema from '../../hoc/withAnomalySchema';

import { getFileUploadTypes, matchPhrases } from '../../../utils/utilities';
import { DamageClassItem, NewAnomalyItem } from '../../../utils/models';
import { HEIGHTS, THREATS, WEIGHTS } from './pdoItemForm';

import { getAffirmativeOptions } from '../../../utils/data';
import { FVI_SCHEMA } from '../../../utils/constants';

export interface AnomalyItemWithPDO extends NewAnomalyItem {
	pdoDescription: string;
	pdoLocation: string;
	hasPDO: string;
	weight: number;
	height: number;
	threat: string;
}

interface Props {
	anomalyItem: AnomalyItemWithPDO;
}

interface State {
	data: AnomalyItemWithPDO;
	errors: object;
	loading: boolean;
	progress: 0;
	damageClasses: DamageClassItem[];
}

const PDO_FORM_FIELDS = {
	hasPDO: Joi.string().valid('yes', 'no').insensitive().required().label('Has Potential Drop Object'),
	pdoDescription: Joi.when('hasPDO', {
		is: Joi.string().valid('yes').insensitive(),
		then: Joi.string().min(3).required().label('PDO Description'),
		otherwise: Joi.string().allow('', 'N/A').optional().label('PDO Description'),
	}),
	pdoLocation: Joi.when('hasPDO', {
		is: Joi.string().valid('yes').insensitive(),
		then: Joi.string().min(3).required().label('PDO Location'),
		otherwise: Joi.string().allow('', 'N/A').optional().label('PDO Location'),
	}),
	weight: Joi.when('hasPDO', {
		is: Joi.string().valid('yes').insensitive(),
		then: Joi.number().required().label('Weight (KG) (Approx.)'),
		otherwise: Joi.string().valid('N/A', '').optional().label('Weight (KG) (Approx.)'),
	}),

	height: Joi.when('hasPDO', {
		is: Joi.string().valid('yes').insensitive(),
		then: Joi.number().required().label('Height (M) (Approx.)'),
		otherwise: Joi.string().valid('N/A', '').optional().label('Height (M) (Approx.)'),
	}),

	threat: Joi.when('hasPDO', {
		is: Joi.string().valid('yes').insensitive(),
		then: Joi.string().min(3).required().label('Threat'),
		otherwise: Joi.string().allow('', 'N/A').optional().label('Threat'),
	}),
};

class AnomalyItemPDOForm extends Form {
	state = {
		data: {
			location: '',
			description: [],
			recommendation: [],
			class: '',
			generalImage: [],
			closeImage: [],
			hasPDO: 'yes',
			pdoDescription: '',
			pdoLocation: '',
			weight: '',
			height: '',
			threat: '',
		},
		errors: {},
		loading: false,
		progress: 0,
		damageClasses: [],
	};

	schema = { ...FVI_SCHEMA, ...PDO_FORM_FIELDS };

	static propTypes = {
		onAdd: PropTypes.func,
		onCancelEdit: PropTypes.func,
		damageClasses: PropTypes.array,
	};

	componentDidMount() {
		const state: any = { damageClasses: [] };

		if (this.props.damageClasses) {
			state.damageClasses = this.props.damageClasses;
		}

		if (this.props.anomalyItem) {
			state.data = _.omit(this.props.anomalyItem, ['rootCause', 'interventionRequired']);
		}

		this.setState(state);
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
		if (this.props.anomalyItem && !_.isEqual(this.props.anomalyItem, prevProps.anomalyItem)) {
			this.setState({ data: this.props.anomalyItem });
		}

		if (prevState.data.hasPDO !== this.state.data.hasPDO && matchPhrases(this.state.data.hasPDO, 'no')) {
			this.setState((state) => ({ data: {...state.data, ...PDO_NA_FIELDS } }));
		}

		if (!_.isEqual(this.schema, this.props.schema)) {
			this.schema = { ...this.props.schema, ...PDO_FORM_FIELDS };
		}
	}

	doSubmit = (): void => {
		this.props.onAdd(this.state.data);

		this.setState({ 
			data: {
				location: '',
				description: [],
				recommendation: [],
				class: '',
				hasPDO: 'yes',
				generalImage: [],
				closeImage: [],
				pdoDescription: '',
				pdoLocation: '',
				weight: '',
				height: '',
				threat: '',
			},
		});

		toastService.success('Anomaly Item Saved Successfully!');
	};

	handleCancelEdit = () => {
		this.setState({
			data: {
				location: '',
				description: [],
				recommendation: [],
				class: '',
				generalImage: [],
				closeImage: [],
				pdoDescription: '',
				pdoLocation: '',
				weight: '',
				height: '',
				threat: '',
			}
		});

		this.props.onCancelEdit();
	}

	mapAnomalyClasses = () => {
		return _.map(this.state.damageClasses, (item) => _.pick(item, ['id', 'name']))
	};

	getSelectedClassDescription = () => {
		const classMatch = _.find(this.state.damageClasses as DamageClassItem[], ['id', parseInt(this.state.data.class)]);
		if (classMatch) return classMatch.descriptions;

		return [];
	};
	
	getSelectedClassRecommendations = () => {
		const classMatch = _.find(this.state.damageClasses as DamageClassItem[], ['id', parseInt(this.state.data.class)]);
		if (classMatch) return classMatch.recommendations;

		return [];
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'location',
							placeholder: 'Enter Anomaly Location',
							label: 'Anomaly Location',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'class',
							placeholder: 'Select Class',
							label: 'Class',
							options: this.mapAnomalyClasses(),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.getSelectedClassDescription(),
							name: 'description',
							placeholder: 'Enter Anomaly Description',
							label: 'Anomaly Description',
							isMulti: true,
						})}
					</div>

					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.getSelectedClassRecommendations(),
							name: 'recommendation',
							placeholder: 'Enter Remark & Recommendation',
							label: 'Remark & Recommendation',
							isMulti: true,
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderFileInput({
							max: this.props.max,
							multiple: this.props.max > 1,
							name: 'generalImage',
							hideCaption: true,
							accept: 'image/*,application/pdf',
							label: 'Anomaly Image (General View)',
							sublabel: 'Upload photos showing general view',
							types: getFileUploadTypes(),
						})}
					</div>

					<div className="col">
						{this.renderFileInput({
							max: this.props.max,
							multiple: this.props.max > 1,
							hideCaption: true,
							name: 'closeImage',
							accept: 'image/*,application/pdf',
							label: 'Anomaly Image (Close View)',
							sublabel: 'Upload photos showing close view',
							types: getFileUploadTypes(),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: getAffirmativeOptions(),
							name: 'hasPDO',
							placeholder: 'Select Has PDO',
							label: 'Has PDO',
						})}
					</div>

					<Conditional isVisible={matchPhrases(this.state.data.hasPDO, 'yes')}>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'pdoLocation',
								placeholder: 'Enter PDO Location',
								label: 'PDO Location',
							})}
						</div>
						
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'pdoDescription',
								placeholder: 'Enter PDO Description',
								label: 'PDO Description',
							})}
						</div>
					</Conditional>
				</div>
				
				<Conditional isVisible={matchPhrases(this.state.data.hasPDO, 'yes')}>
					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								options: WEIGHTS,
								name: 'weight',
								placeholder: 'Select Weight',
								label: 'Weight (KG) (Approx.)',
							})}
						</div>
						
						<div className="col">
							{this.renderSelect({
								options: HEIGHTS,
								name: 'height',
								placeholder: 'Select Height',
								label: 'Height (M) (Approx.)',
							})}
						</div>
						
						<div className="col">
							{this.renderSelect({
								options: THREATS,
								name: 'threat',
								placeholder: 'Select Threat',
								label: 'Threat',
							})}
						</div>
					</div>
				</Conditional>

				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save')}

					<Conditional isVisible={!!this.props.onCancelEdit}>
						<div className="mx-4">
							<Button
								label="Cancel"
								style={{ backgroundColor: 'red' }}
								onClick={this.handleCancelEdit}
							/>
						</div>
					</Conditional>
				</div>
			</form>
		);
	}
}

const PDO_NA_FIELDS = {
  pdoDescription: 'N/A',
  pdoLocation:    'N/A',
  weight:         'N/A',
  height:         'N/A',
  threat:         'N/A',
} as const;

export default withAnomalySchema(AnomalyItemPDOForm);
